import { useState, useEffect, useCallback } from "react";
import { Inbox, RefreshCw, Paperclip, ChevronLeft, Search, X, Clock, Trash2 } from "lucide-react";
import Spinner from "@/components/Spinner";
import clsx from "clsx";

import { API_URL } from "@/lib/siteConfig";
const API_BASE = API_URL;

interface InboxMessage {
  id: string;
  message_id: string | null;
  from_address: string;
  to_address: string | null;
  subject: string | null;
  received_at: string;
  is_reply: number;
  processed: number;
  campaign_id: string | null;
  campaign_name: string | null;
  body_text: string | null;
  body_html: string | null;
  attachments: any[] | null;
}

async function apiFetch<T>(path: string): Promise<T> {
  const token = localStorage.getItem("moremail_token");
  const res = await fetch(`${API_BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

async function apiDelete(path: string): Promise<void> {
  const token = localStorage.getItem("moremail_token");
  await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function extractName(from: string): string {
  const match = from.match(/^"?([^"<]+)"?\s*</);
  if (match) return match[1].trim();
  return from.split("@")[0] || "Unknown";
}

function extractEmail(from: string): string {
  const match = from.match(/<([^>]+)>/);
  if (match) return match[1];
  return from;
}

function extractDomain(to: string | null): string {
  if (!to) return "";
  const email = to.match(/<([^>]+)>/) ? to.match(/<([^>]+)>/)![1] : to.split(",")[0].trim();
  return "@" + (email.split("@")[1] || "");
}

function getInitials(name: string): string {
  return name.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

const COLORS = [
  "bg-blue-500", "bg-teal", "bg-amber-500", "bg-purple-500",
  "bg-rose-500", "bg-emerald-500", "bg-cyan-500", "bg-orange-500",
];
function avatarColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = email.charCodeAt(i) + ((hash << 5) - hash);
  return COLORS[Math.abs(hash) % COLORS.length];
}

export default function AdminInboxPage() {
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<InboxMessage | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [showUnread, setShowUnread] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: "50", offset: "0" });
      if (searchDebounced) params.set("search", searchDebounced);
      if (showUnread) params.set("unread", "true");

      const data = await apiFetch<{ count: number; total: number; messages: InboxMessage[] }>(
        `/inbox/messages?${params}`
      );
      setMessages(data.messages);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || "Failed to load inbox");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchDebounced, showUnread]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: string) => {
    await apiDelete(`/inbox/messages/${id}`);
    setMessages((prev) => prev.filter((m) => m.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  // Mark as read when opening
  const handleSelect = async (msg: InboxMessage) => {
    setSelected(msg);
    if (!msg.processed) {
      try {
        await apiFetch(`/inbox/messages/${msg.id}`);
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, processed: 1 } : m))
        );
      } catch {}
    }
  };

  if (selected) {
    const attachments = Array.isArray(selected.attachments) ? selected.attachments : [];

    return (
      <div className="h-full flex flex-col">
        <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-4 flex items-center gap-4">
          <button onClick={() => setSelected(null)}
            className="p-2 -ml-2 rounded-lg text-slate hover:bg-black/[0.04]">
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate">{selected.subject || "(no subject)"}</h1>
            <p className="text-sm text-slate truncate">
              From: {extractName(selected.from_address)} &lt;{extractEmail(selected.from_address)}&gt;
            </p>
          </div>
          <button onClick={() => handleDelete(selected.id)}
            className="p-2 rounded-lg text-red-400 hover:bg-red-50" title="Delete">
            <Trash2 size={18} />
          </button>
          <div className="text-xs text-slate flex items-center gap-1.5">
            <Clock size={13} />
            {new Date(selected.received_at).toLocaleString()}
          </div>
        </div>

        <div className="px-8 py-4 border-b border-black/[0.06] bg-white">
          <div className="flex items-start gap-4">
            <div className={clsx("w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0",
              avatarColor(extractEmail(selected.from_address)))}>
              {getInitials(extractName(selected.from_address))}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="font-semibold">{extractName(selected.from_address)}</span>
                <span className="text-xs text-slate">{extractEmail(selected.from_address)}</span>
              </div>
              <div className="text-xs text-slate mt-0.5">
                To: {selected.to_address || "—"}
              </div>
              {selected.campaign_name && (
                <div className="text-xs text-teal mt-1">
                  Reply to campaign: {selected.campaign_name}
                </div>
              )}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {attachments.map((a: any, i: number) => (
                    <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-paper rounded-lg text-xs text-slate border border-black/[0.06]">
                      <Paperclip size={12} />
                      {a.filename || "attachment"} {a.size ? `(${(a.size / 1024).toFixed(1)}KB)` : ""}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-8">
          {selected.body_html ? (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-6 max-w-3xl">
              <iframe
                srcDoc={`<!DOCTYPE html><html><head><style>body{font-family:-apple-system,system-ui,sans-serif;font-size:14px;color:#333;line-height:1.6;margin:0;padding:0;}a{color:#0A8F7F;}img{max-width:100%;height:auto;}</style></head><body>${selected.body_html}</body></html>`}
                className="w-full border-0"
                style={{ minHeight: "400px" }}
                sandbox="allow-same-origin"
                title="Email content"
                onLoad={(e) => {
                  const iframe = e.target as HTMLIFrameElement;
                  if (iframe.contentDocument) {
                    iframe.style.height = iframe.contentDocument.body.scrollHeight + 20 + "px";
                  }
                }}
              />
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-black/[0.06] p-6 max-w-3xl">
              <pre className="whitespace-pre-wrap text-sm text-ink font-sans leading-relaxed">
                {selected.body_text || "(empty message)"}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur border-b border-black/[0.06] px-8 py-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold">Inbox</h1>
          <p className="text-sm text-slate">
            {total} message{total !== 1 ? "s" : ""} · Catch-all for all domains
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowUnread(!showUnread)}
            className={clsx("px-4 py-2.5 rounded-full text-sm font-semibold border transition-colors",
              showUnread ? "bg-gray-900 text-white border-gray-900" : "bg-white text-slate border-black/[0.08] hover:border-black/20"
            )}>
            Unread Only
          </button>
          <button onClick={() => load(false)} disabled={refreshing}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-md shadow-coral/20 disabled:opacity-50">
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      <div className="p-8 space-y-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search from, to, subject…"
              className="w-full pl-9 pr-8 py-2 rounded-lg border border-black/[0.08] text-sm bg-white focus:outline-none focus:border-coral focus:ring-1 focus:ring-coral/20"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate hover:text-ink">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl">{error}</div>
        )}

        {loading ? (
          <Spinner className="py-20" />
        ) : messages.length === 0 ? (
          <div className="bg-white rounded-2xl border border-black/[0.06] py-20 text-center">
            <Inbox size={40} className="mx-auto text-slate/30 mb-3" />
            <p className="text-slate font-medium">
              {search ? "No messages match your search" : "No messages yet"}
            </p>
            <p className="text-xs text-slate/60 mt-1">
              {!search && "Send a test email to any address at your hosted domains"}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden divide-y divide-black/[0.04]">
            {messages.map((msg) => (
              <button
                key={msg.id}
                onClick={() => handleSelect(msg)}
                className={clsx(
                  "w-full flex items-center gap-4 px-6 py-4 hover:bg-black/[0.015] transition-colors text-left",
                  !msg.processed && "bg-blue-50/30"
                )}
              >
                <div className={clsx("w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0",
                  avatarColor(extractEmail(msg.from_address)))}>
                  {getInitials(extractName(msg.from_address))}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={clsx("text-sm truncate", !msg.processed ? "font-bold" : "font-semibold")}>
                      {extractName(msg.from_address)}
                    </span>
                    <span className="text-[11px] text-slate truncate hidden sm:inline">
                      {extractEmail(msg.from_address)}
                    </span>
                    {!msg.processed && (
                      <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                    )}
                  </div>
                  <div className={clsx("text-sm truncate", !msg.processed && "font-semibold")}>
                    {msg.subject || "(no subject)"}
                  </div>
                  <div className="text-xs text-slate truncate mt-0.5">
                    {(msg.body_text || "").slice(0, 100)}
                  </div>
                </div>

                <div className="hidden md:block text-right shrink-0">
                  <span className="text-[11px] font-mono text-slate bg-paper px-2 py-0.5 rounded-full">
                    {extractDomain(msg.to_address)}
                  </span>
                </div>

                {msg.is_reply ? (
                  <span className="text-[10px] font-semibold text-teal bg-teal-light px-1.5 py-0.5 rounded shrink-0">Reply</span>
                ) : null}

                <div className="text-xs text-slate whitespace-nowrap shrink-0 w-16 text-right">
                  {formatDate(msg.received_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
